import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BarcodeReader from 'react-barcode-reader';
import api from '../utils/api';
import { StockItem, Warehouse } from '../types';
import { getDisplayName } from '../utils/stockUtils';
import '../core.css';

interface ScannedItem {
  gtin: string | undefined;
  qty: number;
  sku: string;
  name: string;
  currentStock: number;
}

interface BarcodeScannerProps {
  warehouseId: string;
  action: 'production' | 'shipping';
  title: string;
  instruction: string;
}

const useBeforeUnload = (shouldPrompt: boolean) => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldPrompt) {
        e.preventDefault();
        e.returnValue = 'You have scanned items that have not been saved, please Save or Cancel';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldPrompt]);
};

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ warehouseId, action, title, instruction }) => {
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState<string>('');
  const [changedStocks, setChangedStocks] = useState<{
    [key: string]: {
      warehouse_stocks?: { [warehouseId: number]: number };
    };
  }>({});
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const isNavigatingRef = useRef(false);
  const lastPathnameRef = useRef(currentLocation.pathname);

  const fetchNeonStock = async () => {
    try {
      const response = await api.get('/neon-stock');
      setStockData(response.data.data);
      setWarehouses(response.data.warehouses || []);
    } catch (error: any) {
      console.error('Fetch neon stock error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        setError(error.response?.data?.error || error.message || 'Failed to fetch stock data');
      }
    }
  };

  useEffect(() => {
    fetchNeonStock();
  }, []);

  useBeforeUnload(scannedItems.length > 0);

  useEffect(() => {
    if (lastPathnameRef.current === currentLocation.pathname || isNavigatingRef.current) {
      return;
    }
    if (scannedItems.length > 0) {
      isNavigatingRef.current = true;
      const shouldLeave = window.confirm(
        'You have unsaved scanned items. Do you want to save changes, cancel, or leave? Click OK to leave (changes will be cleared).'
      );
      if (!shouldLeave) {
        navigate(lastPathnameRef.current, { replace: true });
        isNavigatingRef.current = false;
        return;
      }
      setScannedItems([]);
      setChangedStocks({});
      isNavigatingRef.current = false;
    }
    lastPathnameRef.current = currentLocation.pathname;
  }, [currentLocation.pathname, scannedItems.length, navigate]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleWarehouseStockChange = (item: StockItem, warehouseId: number, value: string) => {
    const key = `${item.id}-${item.type}`;
    let newValue: number;
    if (value === '' || isNaN(parseInt(value, 10))) {
      newValue = 0;
    } else {
      newValue = parseInt(value, 10);
      if (newValue < -50) {
        alert('Stock cannot be less than -50.');
        return;
      }
    }
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        warehouse_stocks: {
          ...(prev[key]?.warehouse_stocks || {}),
          [warehouseId]: newValue,
        },
      },
    }));
    const updatedItem = {
      ...item,
      warehouse_stocks: {
        ...item.warehouse_stocks,
        [warehouseId]: newValue,
      },
    };
    setStockData((prev) =>
      prev.map((dataItem) =>
        dataItem.id === updatedItem.id && dataItem.type === updatedItem.type
          ? updatedItem
          : dataItem
      )
    );
  };

  const handleScan = (data: string) => {
    console.log('Scanned GTIN:', data);
    const gtin = data.trim().slice(-12);
    const item = stockData.find((stock) => stock.gtin === gtin);
    if (!item) {
      setError(`GTIN ${gtin} not found in inventory`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    const currentStock = item.warehouse_stocks[parseInt(warehouseId)] ?? 0;
    const qtyChange = action === 'production' ? 1 : -1;
    const newStock = Math.max(currentStock + qtyChange, -50);
    setScannedItems((prev) => {
      const existing = prev.find((scanned) => scanned.gtin === gtin);
      if (existing) {
        return prev.map((scanned) =>
          scanned.gtin === gtin ? { ...scanned, qty: scanned.qty + qtyChange } : scanned
        );
      }
      return [
        ...prev,
        {
          gtin,
          qty: qtyChange,
          sku: item.sku,
          name: getDisplayName(item, stockData, stockData),
          currentStock,
        },
      ];
    });
    handleWarehouseStockChange(item, parseInt(warehouseId), String(newStock));
  };

  const handleError = (err: string) => {
    console.log('Scanner Error:', err);
    setError(err);
    setTimeout(() => setError(null), 3000);
  };

  const handleManualScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput) {
      handleScan(manualInput);
      setManualInput('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleSave = async () => {
    try {
      await handleSaveAll();
      setScannedItems((prev) =>
        prev.map((item) => {
          const stockItem = stockData.find((stock) => stock.gtin === item.gtin);
          return stockItem
            ? { ...item, currentStock: stockItem.warehouse_stocks[parseInt(warehouseId)] ?? 0 }
            : item;
        })
      );
      setScannedItems([]);
      alert('Inventory updated successfully');
    } catch (err: any) {
      setError('Failed to save inventory changes');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleCancel = () => {
    setScannedItems([]);
    scannedItems.forEach((scanned) => {
      const item = stockData.find((stock) => stock.gtin === scanned.gtin);
      if (item) {
        handleWarehouseStockChange(item, parseInt(warehouseId), String(scanned.currentStock));
      }
    });
    setChangedStocks({});
  };

  const handleSaveAll = async () => {
    const warehouseUpdates: any[] = [];
    for (const [key, changes] of Object.entries(changedStocks)) {
      const [itemId, itemType] = key.split('-');
      const item = stockData.find(
        (dataItem) => dataItem.id === parseInt(itemId) && dataItem.type === itemType
      );
      if (!item) continue;
      if (changes.warehouse_stocks) {
        Object.entries(changes.warehouse_stocks).forEach(([wid, stockValue]) => {
          const stockQuantity = Number(stockValue);
          const warehouseIdNum = parseInt(wid);
          warehouseUpdates.push({
            product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
            variation_id: item.type === 'variation' ? item.id : 0,
            warehouse_id: warehouseIdNum,
            stock_quantity: isNaN(stockQuantity) ? 0 : Math.max(stockQuantity, -50),
          });
        });
      }
    }
    try {
      // Update stock
      await api.post('/update-stock', {
        warehouse_updates: warehouseUpdates,
        avg_sales_updates: [],
        target_stock_days_updates: [],
        supplier_updates: [],
        gtin_updates: [],
      });

      // Log stock transactions
      for (const item of scannedItems) {
        const stockItem = stockData.find((stock) => stock.gtin === item.gtin);
        if (stockItem) {
          await api.post('/add-stock-transaction', {
            product_id: stockItem.type === 'variation' && stockItem.parent_product_id ? stockItem.parent_product_id : stockItem.id,
            variation_id: stockItem.type === 'variation' ? stockItem.id : 0,
            warehouse_id: parseInt(warehouseId),
            quantity: Math.abs(item.qty),
            reason: `Barcode scan ${action === 'production' ? 'addition' : 'shipping'}`,
            transaction_type: action === 'production' ? 'ADD' : 'REMOVE',
          });
        }
      }

      setChangedStocks({});
      await fetchNeonStock();
    } catch (error: any) {
      console.error('Update stock error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        throw new Error(error.response?.data?.error || error.message || 'Failed to update stock');
      }
    }
  };

  const warehouseName = warehouses.find(w => w.id === parseInt(warehouseId))?.name || `Warehouse ${warehouseId}`;

  return (
    <div className="container barcode-scanner-container">
      <h1>{title} - {warehouseName}</h1>
      <p>{instruction}</p>
      <BarcodeReader onScan={handleScan} onError={handleError} />
      {error && <div className="error-message">{error}</div>}
      <div className="scanned-items-table">
        <table>
          <thead>
            <tr>
              <th>Qty {action === 'production' ? 'Added' : 'Removed'}</th>
              <th>Product SKU</th>
              <th>Product Name</th>
              <th>Current {warehouseName} Stock</th>
            </tr>
          </thead>
          <tbody>
            {scannedItems.map((item) => (
              <tr key={item.gtin}>
                <td className="quantity-cell">{item.qty}</td>
                <td>{item.sku}</td>
                <td>{item.name}</td>
                <td>{item.currentStock}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="action-buttons">
        <button type="button" className="action-button" onClick={handleSave} disabled={scannedItems.length === 0}>
          Save / Enregistrer
        </button>
        <button type="button" className="action-button cancel-button" onClick={handleCancel} disabled={scannedItems.length === 0}>
          Cancel / Annuler
        </button>
      </div>
    </div>
  );
};

export default BarcodeScanner;